import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarRange, IndianRupee, RotateCcw, Mountain, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import AdminLayout from "./AdminLayout";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-md border border-primary/10 bg-card p-5" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`grid place-items-center w-9 h-9 rounded-md ${accent ? "bg-accent/10 text-accent" : "bg-secondary text-foreground"}`}><Icon size={18} /></span>
      </div>
      <p className="font-display text-4xl text-foreground mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <AdminLayout title="Dashboard">
      {!stats ? (
        <div className="grid gap-4 md:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-md bg-secondary/60 animate-pulse" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Stat icon={IndianRupee} label="Revenue" value={inr(stats.revenue)} accent />
            <Stat icon={CalendarRange} label="Total bookings" value={stats.total_bookings} />
            <Stat icon={CheckCircle2} label="Confirmed" value={stats.confirmed_bookings} />
            <Stat icon={RotateCcw} label="Refunded" value={stats.refunded_count} />
            <Stat icon={Mountain} label="Experiences" value={stats.experiences_count} />
            <Stat icon={MessageSquare} label="New enquiries" value={stats.enquiries_count} />
          </div>

          {stats.bookings_by_type && Object.keys(stats.bookings_by_type).length > 0 && (
            <div className="mt-6 rounded-md border border-primary/10 bg-card p-5">
              <h2 className="font-semibold text-foreground mb-3">Paid bookings by type</h2>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.bookings_by_type).map(([t, n]) => (
                  <span key={t} className="rounded-full bg-secondary px-4 py-1.5 text-sm capitalize text-foreground">{t}: <b>{n}</b></span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 rounded-md border border-primary/10 bg-card">
            <div className="flex items-center justify-between p-5 border-b border-primary/10">
              <h2 className="font-semibold text-foreground">Recent bookings</h2>
              <Link to="/admin/bookings" className="text-sm text-accent font-semibold inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
            </div>
            {recent.length === 0 ? <p className="p-8 text-center text-muted-foreground">No bookings yet.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-primary/10">
                  <th className="px-5 py-3 font-medium">Customer</th><th className="px-5 py-3 font-medium">Item</th>
                  <th className="px-5 py-3 font-medium">Type</th><th className="px-5 py-3 font-medium text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {recent.map((b) => (
                    <tr key={b.id} className="border-b border-primary/5 last:border-0">
                      <td className="px-5 py-3 text-foreground font-medium">{b.customer_name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{b.title}</td>
                      <td className="px-5 py-3 text-muted-foreground capitalize">{b.type}</td>
                      <td className="px-5 py-3 text-right text-foreground font-medium">{inr(b.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
